import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Trash,
  LayoutSidebar,
  X,
  BoxArrowRight,
  Person,
} from 'react-bootstrap-icons';
import { connect } from 'react-redux';
import clsx from 'clsx';
import { logout } from '../actions/auth';
import { resolveProfilePic, userDisplayName } from '../utils/profile';
import { useChatShell } from '../layouts/ChatShell';

function normalizeRole(msg) {
  if (msg.role) return msg.role;
  if (msg.user === 'assistant') return 'assistant';
  return 'questioner';
}

function messagePreviewText(msg) {
  const text = (msg.message || msg.content || '').trim();
  if (!text) return null;
  return text.length > 40 ? `${text.slice(0, 40)}…` : text;
}

function parsePrompt(prompt) {
  if (Array.isArray(prompt)) return prompt;
  if (typeof prompt === 'string') {
    try {
      const parsed = JSON.parse(prompt);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function historyLabel(item) {
  try {
    const messages = parsePrompt(item.prompt);
    for (const msg of messages) {
      if (normalizeRole(msg) === 'questioner') {
        const preview = messagePreviewText(msg);
        if (preview) return preview;
      }
    }
    for (const msg of messages) {
      const preview = messagePreviewText(msg);
      if (preview) return preview;
    }
  } catch {
    // fall through
  }
  const generated = item.generated;
  if (generated && typeof generated === 'object') {
    const preview = messagePreviewText(generated);
    if (preview) return preview;
  }
  return 'New chat';
}

function firstQuestionerKey(item) {
  try {
    const messages = parsePrompt(item.prompt);
    for (const msg of messages) {
      if (normalizeRole(msg) === 'questioner') {
        return (msg.message || msg.content || '').trim().toLowerCase();
      }
    }
  } catch {
    // fall through
  }
  return `id:${item._id}`;
}

function messageCount(item) {
  try {
    return parsePrompt(item.prompt).length + (item.generated ? 1 : 0);
  } catch {
    return 0;
  }
}

/** Collapse duplicate turn-rows that share the same first questioner message. */
function dedupeThreads(items) {
  const groups = new Map();
  for (const item of items) {
    const key = firstQuestionerKey(item) || `id:${item._id}`;
    const existing = groups.get(key);
    if (!existing) {
      groups.set(key, {
        id: item._id,
        label: historyLabel(item),
        created_at: item.created_at,
        ids: [item._id],
        messageCount: messageCount(item),
      });
      continue;
    }
    existing.ids.push(item._id);
    const count = messageCount(item);
    if (count >= existing.messageCount) {
      existing.id = item._id;
      existing.label = historyLabel(item);
      existing.created_at = item.created_at;
      existing.messageCount = count;
    }
  }
  return Array.from(groups.values());
}

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function dateGroupLabel(iso) {
  if (!iso) return 'Older';
  const created = new Date(iso);
  if (Number.isNaN(created.getTime())) return 'Older';
  const today = startOfDay(new Date());
  const day = startOfDay(created);
  const diffDays = Math.round((today - day) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return 'Previous 7 days';
  return 'Older';
}

const GROUP_ORDER = ['Today', 'Yesterday', 'Previous 7 days', 'Older'];

function Sidebar({
  setChatLog,
  setFullChatLog,
  onNewChat,
  historyVersion,
  user,
  logout,
}) {
  const token = localStorage.getItem('access');
  const [rawChats, setRawChats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const { sidebarOpen, setSidebarOpen, activeChatId, setActiveChatId, openProfile } =
    useChatShell();

  const logoutfunc = () => {
    logout();
    navigate('/');
  };

  const loadHistory = useCallback(() => {
    if (user == null) return;
    setLoading(true);
    fetch(`${process.env.REACT_APP_API_URL}/api/chats/${user.id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Failed to load chats (${response.status})`);
        }
        return response.json();
      })
      .then((data) => {
        setRawChats(Array.isArray(data) ? data : []);
      })
      .catch((error) => {
        console.error(error);
        setRawChats([]);
      })
      .finally(() => setLoading(false));
  }, [user, token]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory, historyVersion]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const threads = useMemo(() => dedupeThreads(rawChats), [rawChats]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return threads;
    return threads.filter((t) => t.label.toLowerCase().includes(q));
  }, [threads, search]);

  const grouped = useMemo(() => {
    const map = {};
    for (const t of filtered) {
      const label = dateGroupLabel(t.created_at);
      if (!map[label]) map[label] = [];
      map[label].push(t);
    }
    return GROUP_ORDER.filter((g) => map[g]?.length).map((g) => ({
      label: g,
      items: map[g],
    }));
  }, [filtered]);

  async function get_chat_history({ id }) {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/chats/${user.id}/${id}/`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (!response.ok) return;
    const data = await response.json();
    const promptDict = data.prompt;
    const bot_response_dict = data.generated;
    if (promptDict == null) return;

    const raw = parsePrompt(promptDict);
    const messages = raw.map((message, index) => ({
      id: `hist-${id}-${index}`,
      role: normalizeRole(message),
      message: message.message || message.content || '',
      attachments: message.attachments,
    }));
    const newAssistantMessage = {
      id: `hist-${id}-assistant`,
      role: 'assistant',
      message: bot_response_dict?.message || '',
    };
    const full = [...messages, newAssistantMessage];
    setChatLog(full);
    if (setFullChatLog) setFullChatLog(full);
    setActiveChatId(id);
    if (window.innerWidth < 1024) setSidebarOpen(false);
  }

  async function delete_chat_history(thread) {
    const ids = thread.ids || [thread.id];
    await Promise.all(
      ids.map((id) =>
        fetch(`${process.env.REACT_APP_API_URL}/api/chats/${user.id}/${id}/`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        })
      )
    );
    setRawChats((prev) => prev.filter((p) => !ids.includes(p._id)));
    setConfirmDeleteId(null);
    if (ids.includes(activeChatId)) {
      setActiveChatId(null);
      setChatLog([]);
      if (setFullChatLog) setFullChatLog([]);
      if (onNewChat) onNewChat();
    }
  }

  const handleNewChat = () => {
    setChatLog([]);
    if (setFullChatLog) setFullChatLog([]);
    if (onNewChat) onNewChat();
    setActiveChatId(null);
    if (window.innerWidth < 1024) setSidebarOpen(false);
  };

  const avatar = resolveProfilePic(user?.profile_pic_url);
  const displayName = userDisplayName(user);

  return (
    <>
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <button
          type="button"
          aria-label="Close sidebar"
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-50 flex w-[280px] flex-col bg-surface text-text-primary transition-transform duration-200 lg:static lg:z-auto',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:hidden'
        )}
      >
        <div className="flex items-center justify-between gap-2 px-3 pt-3">
          <img
            src="/static/images/logo-mark.png"
            alt="SchoolCracked"
            className="h-auto w-[190px] object-contain"
          />
          <button
            type="button"
            className="rounded-lg p-2 text-text-muted hover:bg-surface-hover hover:text-text-primary"
            onClick={() => setSidebarOpen(false)}
            aria-label="Collapse sidebar"
            title="Collapse sidebar"
          >
            <LayoutSidebar size={18} />
          </button>
        </div>

        <div className="px-3 pt-3">
          <button
            type="button"
            onClick={handleNewChat}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-accent px-3 py-2.5 text-sm font-medium text-white transition hover:bg-accent-hover"
          >
            <Plus size={18} />
            New chat
          </button>
        </div>

        <div className="px-3 pt-3">
          <div className="relative">
            <Search
              size={14}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
            />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search chats"
              className="w-full rounded-lg border border-border-subtle bg-surface-hover py-2 pl-9 pr-3 text-sm text-text-primary placeholder:text-text-muted outline-none focus:border-accent"
            />
          </div>
        </div>

        <div className="mt-3 flex-1 overflow-y-auto px-2 pb-3 scrollbar-thin">
          {loading && (
            <div className="space-y-2 px-1">
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="h-9 animate-pulse rounded-lg bg-surface-hover"
                />
              ))}
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <p className="px-3 py-6 text-center text-sm text-text-muted">
              {search ? 'No matching chats' : 'No chats yet'}
            </p>
          )}

          {!loading &&
            grouped.map((group) => (
              <div key={group.label} className="mb-3">
                <div className="px-2 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
                  {group.label}
                </div>
                <ul className="m-0 list-none space-y-0.5 p-0">
                  {group.items.map((thread) => {
                    const isActive = activeChatId === thread.id;
                    const confirming = confirmDeleteId === thread.id;
                    return (
                      <li
                        key={thread.id}
                        className={clsx(
                          'chat-history-row flex items-center rounded-lg pr-1 text-sm transition',
                          isActive
                            ? 'bg-surface-elevated text-text-primary'
                            : 'text-text-primary/90 hover:bg-surface-hover'
                        )}
                      >
                        <button
                          type="button"
                          onClick={() => get_chat_history({ id: thread.id })}
                          className="min-w-0 flex-1 truncate py-2 pl-2.5 pr-1 text-left"
                          title={thread.label}
                        >
                          {thread.label}
                        </button>
                        <div
                          className={clsx(
                            'chat-history-actions flex shrink-0 items-center gap-0.5',
                            confirming && 'is-confirming'
                          )}
                        >
                          {confirming ? (
                            <>
                              <button
                                type="button"
                                className="rounded px-1.5 py-1 text-[11px] font-medium text-red-300 hover:text-red-200"
                                onClick={() => delete_chat_history(thread)}
                              >
                                Delete?
                              </button>
                              <button
                                type="button"
                                className="flex h-6 w-6 items-center justify-center rounded text-text-muted hover:text-text-primary"
                                onClick={() => setConfirmDeleteId(null)}
                                aria-label="Cancel delete"
                              >
                                <X size={13} />
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              className="flex h-6 w-6 items-center justify-center rounded text-text-muted hover:text-red-300"
                              onClick={() => setConfirmDeleteId(thread.id)}
                              aria-label="Delete chat"
                              title="Delete"
                            >
                              <Trash size={13} />
                            </button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
        </div>

        <div className="relative border-t border-border-subtle/40 p-3" ref={menuRef}>
          {menuOpen && (
            <div className="absolute bottom-full left-3 right-3 mb-2 overflow-hidden rounded-xl border border-border-subtle bg-surface-elevated shadow-composer">
              <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2.5 text-sm text-text-primary hover:bg-surface-hover"
                onClick={() => {
                  setMenuOpen(false);
                  openProfile();
                }}
              >
                <Person size={16} />
                Edit profile
              </button>
              <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2.5 text-sm text-red-300 hover:bg-surface-hover"
                onClick={logoutfunc}
              >
                <BoxArrowRight size={16} />
                Log out
              </button>
            </div>
          )}
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left hover:bg-surface-hover"
          >
            <img
              src={avatar}
              alt=""
              className="h-9 w-9 rounded-full object-cover bg-surface-elevated"
            />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">{displayName}</div>
              <div className="truncate text-xs text-text-muted">{user?.email}</div>
            </div>
          </button>
        </div>
      </aside>
    </>
  );
}

const mapStateToProps = (state) => ({
  user: state.auth.user,
});

export default connect(mapStateToProps, { logout })(Sidebar);
